import { noop } from 'lodash';
import PT from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import { BULK_RESTORE_LIMIT } from '~/common/constants/max_lengths';
import { Box } from '~/eds/src/components/Box/Box';
import { Modal } from '~/eds/src/components/Modal/Modal';

import Alert from '../Alert/Alert';
import FolderTree, { folderTreePropType } from '../FolderTree/FolderTree';

function FolderModal({
  disabled,
  folderTreeProps,
  title,
  visible,
  onCancel,
  onHide,
  onSelectFolder,
  modalWidth = 'l',
  count,
}) {
  const intl = useIntl();

  const actionButton = {
    disabled,
    errorHandler: noop,
    text: intl.formatMessage({
      id: 'FolderModal.selectFolder',
      defaultMessage: 'Select Folder',
      description: 'Button text to select a folder',
    }),
    promise: async () => onSelectFolder(),
  };

  const renderLimitWarning = () => {
    if (count > BULK_RESTORE_LIMIT) {
      return (
        <Box mb={5}>
          <Alert enableIcon variant="warning">
            {intl.formatMessage(
              {
                id: 'FolderModal.bulkRestoreLimit',
                defaultMessage:
                  'Only {limit} documents can be restored at a time. Please select fewer documents to complete this action.',
                description:
                  'Warning message for bulk restore document limit. Example: limit = "100"',
              },
              { limit: BULK_RESTORE_LIMIT },
            )}
          </Alert>
        </Box>
      );
    } else {
      return null;
    }
  };

  return (
    <Modal
      primaryAction={{
        disabled,
        text: intl.formatMessage({
          id: 'FolderModal.selectFolder',
          defaultMessage: 'Select Folder',
          description: 'Button text to select a folder',
        }),
        onClick: async () => {
          onSelectFolder();
          onHide();
        },
      }}
      disableHideOnEscape={true}
      disableHideOnClickOutside={true}
      title={title}
      isVisible={visible}
      width={modalWidth}
      onCancel={onCancel}
      onHide={onHide}
    >
      {renderLimitWarning()}
      <FolderTree {...folderTreeProps} />
    </Modal>
  );
}

FolderModal.propTypes = {
  /** Disables selection of folder */
  disabled: PT.bool,
  /** FolderTree props */
  folderTreeProps: PT.shape(folderTreePropType).isRequired,
  /** Modal title text */
  title: PT.string.isRequired,
  /** Controls modal visibility */
  visible: PT.bool,
  /** Callback function when modal is cancelled (close icon or autohide) */
  onCancel: PT.func,
  /** Callback function to trigger logic for hiding modal */
  onHide: PT.func,
  /** Callback when folder is selected */
  onSelectFolder: PT.func,
  /** Set Modal width. Default to large */
  modalWidth: PT.oneOf(['m', 'l']),
};

export default FolderModal;
